import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useParams, useNavigate, useLocation } from 'react-router-dom';
import { useTenantFromUrl } from '../hooks/useTenantFromUrl';
import { useAutoSave } from '../hooks/useAutoSave';
import { transferAPI, clientAPI, staffAPI } from '../services/api';
import { hasPermission } from '../utils/permissions';
import { formatDisplayDate } from '../utils/dateFormat';
import './TransferDetail.css';

const STATUS_OPTIONS = ['Opened', 'In Review', 'Ready to Send', 'File Sent', 'Confirmed Receipt', 'Voided'];
const TRANSFER_TYPES = ['USPS', 'SecureEmail', 'Fax'];

const MILESTONE_FIELDS = [
  { key: 'transfer_form_date', label: 'Transfer Form Date' },
  { key: 'printed_casenotes_date', label: 'Printed Casenotes Date' },
  { key: 'updated_address_cs_date', label: 'Updated Address CS Date' },
  { key: 'verification_step_4_date', label: 'Verification step 4 (all correspondence in VF)' },
  { key: 'verification_step_5_date', label: 'Verification step 5 (insurance/PA42)' },
  { key: 'verification_step_6_date', label: 'Verification step 6 (family fee report)' },
  { key: 'verification_step_7_date', label: 'Verification step 7 (script)' },
  { key: 'verification_step_8_date', label: 'Verification step 8 (consent forms)' },
];

function toDateInput(v) {
  if (!v) return '';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return '';
  return d.toISOString().slice(0, 10);
}

function TransferDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { tenantPath } = useTenantFromUrl();
  const isNew = !id || id === 'new';

  const [transfer, setTransfer] = useState(null);
  const [staff, setStaff] = useState([]);
  const [clientSearch, setClientSearch] = useState('');
  const [clientResults, setClientResults] = useState([]);
  const [clientSearchLoading, setClientSearchLoading] = useState(false);
  const [clientDropdownOpen, setClientDropdownOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const clientBlurTimerRef = useRef(null);
  const [receivingCfcOptions, setReceivingCfcOptions] = useState({ receiving_cfc_options: [] });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [saveMessage, setSaveMessage] = useState(null);
  const [errorFields, setErrorFields] = useState([]);
  const [saveTrigger, setSaveTrigger] = useState(0);

  const [form, setForm] = useState({
    client_id: '',
    transfer_open_date: '',
    receiving_cfc: '',
    ifsp_start_date: '',
    ifsp_end_date: '',
    transfer_type: '',
    status: '',
    record_ready_to_send_date: '',
    file_sent_date: '',
    who_sent_staff_id: '',
    comment: '',
    transfer_form_date: '',
    printed_casenotes_date: '',
    updated_address_cs_date: '',
    verification_step_4_date: '',
    verification_step_5_date: '',
    verification_step_6_date: '',
    verification_step_7_date: '',
    verification_step_8_date: '',
  });

  useEffect(() => {
    const n = location.state?.inboxNotificationsCreated;
    const statusUpdated = location.state?.referralStatusUpdated;
    const parts = ['Saved.'];
    if (n != null && n > 0) parts.push(`Inbox notifications sent to ${n} person(s).`);
    if (statusUpdated) parts.push('Referral status updated to Transferring Out.');
    if (parts.length > 1) setSaveMessage(parts.join(' '));
  }, [location.state]);

  useEffect(() => {
    if (!hasPermission('transfers.view') && !hasPermission('transfers.create')) {
      navigate(tenantPath('/dashboard'), { replace: true });
      return;
    }
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const [optRes, staffRes] = await Promise.all([
          transferAPI.getOptionsReceivingCfc(),
          staffAPI.getAll({ active: 'true' }),
        ]);
        setReceivingCfcOptions(optRes.data?.data ?? { receiving_cfc_options: [] });
        setStaff(staffRes.data?.data ?? []);

        if (isNew) {
          if (!hasPermission('transfers.create')) {
            navigate(tenantPath('/lists/transfers'), { replace: true });
            return;
          }
          setLoading(false);
          return;
        }

        const tRes = await transferAPI.getById(id);
        const t = tRes.data?.data;
        if (!t) {
          setError('Transfer not found');
          setLoading(false);
          return;
        }
        setTransfer(t);
        setForm({
          client_id: t.client_id || t.client?.id || '',
          transfer_open_date: toDateInput(t.transfer_open_date),
          receiving_cfc: t.receiving_cfc != null ? String(t.receiving_cfc) : '',
          ifsp_start_date: toDateInput(t.ifsp_start_date),
          ifsp_end_date: toDateInput(t.ifsp_end_date),
          transfer_type: t.transfer_type || '',
          status: t.status || '',
          record_ready_to_send_date: toDateInput(t.record_ready_to_send_date),
          file_sent_date: toDateInput(t.file_sent_date),
          who_sent_staff_id: t.who_sent_staff_id != null ? String(t.who_sent_staff_id) : '',
          comment: t.comment || '',
          transfer_form_date: toDateInput(t.transfer_form_date),
          printed_casenotes_date: toDateInput(t.printed_casenotes_date),
          updated_address_cs_date: toDateInput(t.updated_address_cs_date),
          verification_step_4_date: toDateInput(t.verification_step_4_date),
          verification_step_5_date: toDateInput(t.verification_step_5_date),
          verification_step_6_date: toDateInput(t.verification_step_6_date),
          verification_step_7_date: toDateInput(t.verification_step_7_date),
          verification_step_8_date: toDateInput(t.verification_step_8_date),
        });
      } catch (err) {
        setError(err.response?.data?.error || err.message || 'Failed to load');
      } finally {
        setLoading(false);
      }
    })();
  }, [id, isNew, navigate, tenantPath]);

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setSaveMessage(null);
    if (errorFields.length) setErrorFields((prev) => prev.filter((f) => f !== field));
    if (!isNew) setSaveTrigger((p) => p + 1);
  };

  const fieldHasError = (fieldName) => errorFields.includes(fieldName);

  const canEdit = isNew ? hasPermission('transfers.create') : hasPermission('transfers.edit');

  const clientDisplay = isNew
    ? (selectedClient
        ? {
            ei_number: selectedClient.ei_number,
            date_of_birth: selectedClient.date_of_birth,
            assigned_staff_name: selectedClient.assigned_staff_last_name && selectedClient.assigned_staff_first_name
              ? `${selectedClient.assigned_staff_last_name}, ${selectedClient.assigned_staff_first_name}` : null,
          }
        : null)
    : transfer?.client
      ? {
          ei_number: transfer.client.ei_number,
          date_of_birth: transfer.client.date_of_birth,
          assigned_staff_name: transfer.client.assigned_staff_name,
        }
      : null;

  const buildPayload = useCallback(() => ({
    client_id: form.client_id || transfer?.client_id,
    transfer_open_date: form.transfer_open_date || null,
    receiving_cfc: form.receiving_cfc === '' ? null : Number(form.receiving_cfc),
    ifsp_start_date: form.ifsp_start_date || null,
    ifsp_end_date: form.ifsp_end_date || null,
    transfer_type: form.transfer_type || null,
    status: form.status || null,
    record_ready_to_send_date: form.record_ready_to_send_date || null,
    file_sent_date: form.file_sent_date || null,
    who_sent_staff_id: form.who_sent_staff_id === '' ? null : Number(form.who_sent_staff_id),
    comment: form.comment || null,
    transfer_form_date: form.transfer_form_date || null,
    printed_casenotes_date: form.printed_casenotes_date || null,
    updated_address_cs_date: form.updated_address_cs_date || null,
    verification_step_4_date: form.verification_step_4_date || null,
    verification_step_5_date: form.verification_step_5_date || null,
    verification_step_6_date: form.verification_step_6_date || null,
    verification_step_7_date: form.verification_step_7_date || null,
    verification_step_8_date: form.verification_step_8_date || null,
  }), [form, transfer?.client_id]);

  const runClientSearch = useCallback((q) => {
    if (!q || String(q).trim().length < 2) { setClientResults([]); return; }
    setClientSearchLoading(true);
    clientAPI.getAll({ q: String(q).trim(), page: 1, pageSize: 500 })
      .then((res) => setClientResults(res.data?.data ?? []))
      .catch(() => setClientResults([]))
      .finally(() => setClientSearchLoading(false));
  }, []);

  useEffect(() => {
    const t = setTimeout(() => runClientSearch(clientSearch), 300);
    return () => clearTimeout(t);
  }, [clientSearch, runClientSearch]);

  const performSave = useCallback(async () => {
    const payload = buildPayload();
    const res = await transferAPI.update(id, payload);
    const inboxCount = res.data?.inboxNotificationsCreated ?? 0;
    setSaveMessage(inboxCount > 0 ? `Saved. Inbox notifications sent to ${inboxCount} person(s).` : 'Saved.');
    const tRes = await transferAPI.getById(id);
    setTransfer(tRes.data?.data ?? null);
  }, [id, buildPayload]);

  useAutoSave(saveTrigger, performSave, { enabled: !isNew && canEdit, delayMs: 500 });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canEdit) return;
    if (isNew && !form.client_id) {
      setError('Client is required');
      return;
    }
    setSaving(true);
    setError(null);
    setSaveMessage(null);
    setErrorFields([]);
    try {
      const payload = buildPayload();
      if (isNew) {
        const res = await transferAPI.create(payload);
        const created = res.data?.data;
        const inboxCount = res.data?.inboxNotificationsCreated ?? 0;
        const referralStatusUpdated = res.data?.referralStatusUpdated ?? false;
        if (created?.id) {
          navigate(tenantPath(`/lists/transfers/${created.id}`), { replace: true, state: { inboxNotificationsCreated: inboxCount, referralStatusUpdated } });
        } else {
          navigate(tenantPath('/lists/transfers'), { replace: true });
        }
      } else {
        await performSave();
      }
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to save');
      setErrorFields(err.response?.data?.errorFields || []);
    } finally {
      setSaving(false);
    }
  };

  const staffName = (s) => [s.last_name, s.first_name].filter(Boolean).join(', ') || `Staff #${s.id}`;
  const clientName = (c) => [c.last_name, c.first_name].filter(Boolean).join(', ') || `Client #${c.id}`;
  const formatDate = (d) => (d ? formatDisplayDate(d) : '—');

  if (loading) {
    return (
      <div className="transfer-detail-page">
        <div className="transfer-detail-header">
          <div>
            <h1>{isNew ? 'New transfer' : 'Transfer'}</h1>
          </div>
        </div>
        <p className="loading-msg">Loading...</p>
      </div>
    );
  }

  if (!isNew && error && !transfer) {
    return (
      <div className="transfer-detail-page">
        <div className="transfer-detail-header">
          <div>
            <h1>Transfer</h1>
          </div>
        </div>
        <p className="error-msg">{error}</p>
      </div>
    );
  }

  const options = receivingCfcOptions.receiving_cfc_options || [];

  return (
    <div className="transfer-detail-page">
      <div className="transfer-detail-header">
        <div>
          <h1>{isNew ? 'New transfer' : `Transfer${transfer?.client ? `: ${[transfer.client.last_name, transfer.client.first_name].filter(Boolean).join(', ')}` : ''}`}</h1>
        </div>
        {isNew && canEdit && (
          <div className="transfer-detail-header-actions">
            <Link to={tenantPath('/lists/transfers')} className="btn-cancel">Cancel</Link>
            <button type="submit" form="transfer-detail-form" className="btn-primary" disabled={saving}>
              {saving ? 'Saving...' : 'Create transfer'}
            </button>
          </div>
        )}
      </div>

      {error && <p className="error-msg">{error}</p>}
      {saveMessage && <p className="save-msg">{saveMessage}</p>}

      <form id="transfer-detail-form" onSubmit={handleSubmit} className="transfer-detail-form">
        <section className="form-section">
          <h2>Client</h2>
          {isNew ? (
            <div className="form-group">
              <label htmlFor="transfer-client-search">Client *</label>
              {form.client_id && selectedClient ? (
                <div className="transfer-client-selected">
                  <span className="transfer-client-selected-label">{clientName(selectedClient)}</span>
                  <button
                    type="button"
                    className="transfer-client-change-btn"
                    onClick={() => {
                      handleChange('client_id', '');
                      setSelectedClient(null);
                      setClientSearch('');
                      setClientResults([]);
                    }}
                  >
                    Change
                  </button>
                </div>
              ) : (
                <div className="transfer-client-search-wrap">
                  <input
                    id="transfer-client-search"
                    type="text"
                    className="transfer-client-search-input"
                    placeholder="Type to search clients (name or EI number)..."
                    value={clientSearch}
                    onChange={(e) => setClientSearch(e.target.value)}
                    onFocus={() => setClientDropdownOpen(true)}
                    onBlur={() => {
                      clearTimeout(clientBlurTimerRef.current);
                      clientBlurTimerRef.current = setTimeout(() => setClientDropdownOpen(false), 200);
                    }}
                    autoComplete="off"
                  />
                  {clientSearchLoading && <span className="transfer-client-search-loading">Searching…</span>}
                  {clientDropdownOpen && (clientSearch.length >= 2 || clientResults.length > 0) && (
                    <ul className="transfer-client-results">
                      {clientResults.length === 0 && !clientSearchLoading && (
                        <li className="transfer-client-no-results">
                          {clientSearch.length < 2 ? 'Type at least 2 characters to search.' : 'No clients found.'}
                        </li>
                      )}
                      {clientResults.map((c) => (
                        <li
                          key={c.id}
                          className="transfer-client-option"
                          onPointerDown={(e) => {
                            e.preventDefault();
                            handleChange('client_id', c.id);
                            setSelectedClient(c);
                            setClientSearch('');
                            setClientResults([]);
                            setClientDropdownOpen(false);
                          }}
                        >
                          {clientName(c)}
                          {c.ei_number && <span className="transfer-client-ei"> EI #{c.ei_number}</span>}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </div>
          ) : (
            <div className="read-only-client">
              <span className="client-name">{transfer?.client ? [transfer.client.last_name, transfer.client.first_name].filter(Boolean).join(', ') : '—'}</span>
              <Link to={tenantPath(`/client/${transfer?.client_id}`)} className="link-client">View client</Link>
            </div>
          )}
          {clientDisplay && (
            <div className="client-read-only-fields">
              <div className="ro-field"><span className="ro-label">EI number</span> {clientDisplay.ei_number ?? '—'}</div>
              <div className="ro-field"><span className="ro-label">Birthdate</span> {clientDisplay.date_of_birth ? formatDate(clientDisplay.date_of_birth) : '—'}</div>
              <div className="ro-field"><span className="ro-label">Assigned staff</span> {clientDisplay.assigned_staff_name ?? '—'}</div>
            </div>
          )}
        </section>

        <section className="form-section">
          <h2>Transfer details</h2>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="transfer_open_date">Transfer open date</label>
              <input
                id="transfer_open_date"
                type="date"
                value={form.transfer_open_date}
                onChange={(e) => handleChange('transfer_open_date', e.target.value)}
                disabled={!canEdit}
                className={fieldHasError('transfer_open_date') ? 'input-invalid' : ''}
              />
            </div>
            <div className="form-group">
              <label htmlFor="receiving_cfc">Receiving CFC</label>
              <select
                id="receiving_cfc"
                value={form.receiving_cfc}
                onChange={(e) => handleChange('receiving_cfc', e.target.value)}
                disabled={!canEdit}
                className={fieldHasError('receiving_cfc') ? 'input-invalid' : ''}
              >
                <option value="">Select...</option>
                {options.map((n) => (
                  <option key={n} value={n}>CFC {n}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label htmlFor="transfer_type">Transfer type</label>
              <select
                id="transfer_type"
                value={form.transfer_type}
                onChange={(e) => handleChange('transfer_type', e.target.value)}
                disabled={!canEdit}
              >
                <option value="">Select...</option>
                {TRANSFER_TYPES.map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="ifsp_start_date">IFSP start date</label>
              <input
                id="ifsp_start_date"
                type="date"
                value={form.ifsp_start_date}
                onChange={(e) => handleChange('ifsp_start_date', e.target.value)}
                disabled={!canEdit}
              />
            </div>
            <div className="form-group">
              <label htmlFor="ifsp_end_date">IFSP end date</label>
              <input
                id="ifsp_end_date"
                type="date"
                value={form.ifsp_end_date}
                onChange={(e) => handleChange('ifsp_end_date', e.target.value)}
                disabled={!canEdit}
              />
            </div>
            <div className="form-group">
              <label htmlFor="status">Status</label>
              <select
                id="status"
                value={form.status}
                onChange={(e) => handleChange('status', e.target.value)}
                disabled={!canEdit}
                className={fieldHasError('status') ? 'input-invalid' : ''}
              >
                <option value="">Select...</option>
                {STATUS_OPTIONS.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="record_ready_to_send_date">Record ready to send date</label>
              <input
                id="record_ready_to_send_date"
                type="date"
                value={form.record_ready_to_send_date}
                onChange={(e) => handleChange('record_ready_to_send_date', e.target.value)}
                disabled={!canEdit}
                className={fieldHasError('record_ready_to_send_date') ? 'input-invalid' : ''}
              />
            </div>
            <div className="form-group">
              <label htmlFor="file_sent_date">File sent date</label>
              <input
                id="file_sent_date"
                type="date"
                value={form.file_sent_date}
                onChange={(e) => handleChange('file_sent_date', e.target.value)}
                disabled={!canEdit}
              />
            </div>
            <div className="form-group">
              <label htmlFor="who_sent_staff_id">Who sent</label>
              <select
                id="who_sent_staff_id"
                value={form.who_sent_staff_id}
                onChange={(e) => handleChange('who_sent_staff_id', e.target.value)}
                disabled={!canEdit}
              >
                <option value="">Select...</option>
                {staff.map((s) => (
                  <option key={s.id} value={s.id}>{staffName(s)}</option>
                ))}
              </select>
            </div>
          </div>
        </section>

        <section className="form-section form-section-milestones">
          <h2>Verification / tracking dates</h2>
          <div className="milestone-grid">
            {MILESTONE_FIELDS.map(({ key, label }) => (
              <div key={key} className="form-group">
                <label htmlFor={key}>{label}</label>
                <input
                  id={key}
                  type="date"
                  value={form[key] ?? ''}
                  onChange={(e) => handleChange(key, e.target.value)}
                  disabled={!canEdit}
                  className={fieldHasError(key) ? 'input-invalid' : ''}
                />
              </div>
            ))}
          </div>
        </section>

        <section className="form-section">
          <div className="form-group">
            <label htmlFor="comment">Comment</label>
            <textarea
              id="comment"
              value={form.comment}
              onChange={(e) => handleChange('comment', e.target.value)}
              disabled={!canEdit}
              rows={4}
              className="input-comment"
            />
          </div>
        </section>

        {isNew && canEdit && (
          <div className="form-actions">
            <Link to={tenantPath('/lists/transfers')} className="btn-cancel">Cancel</Link>
            <button type="submit" className="btn-primary" disabled={saving}>
              {saving ? 'Saving...' : 'Create transfer'}
            </button>
          </div>
        )}
      </form>
    </div>
  );
}

export default TransferDetail;
